import {
  Stack,
  Card,
  Table,
  Badge,
  Group,
  Button,
  Text,
  TextInput,
  Alert,
  ActionIcon,
  Modal,
  Select,
  PasswordInput,
  Menu,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { useEffect, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  IconUser,
  IconSearch,
  IconAlertCircle,
  IconCheck,
  IconPencil,
  IconTrash,
  IconPower,
  IconChevronDown,
} from '@tabler/icons-react';
import {
  useUsuarios,
  useCreateUsuario,
  useUpdateUsuario,
  useDeleteUsuario,
  getErrorMessage,
} from '../../api/hooks';
import type { CreateUsuarioDto, UpdateUsuarioDto, Usuario, UsuarioRol } from '../../api/types';
import { CatalogPageHeader } from '../../components/CatalogPageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';

/**
 * Roles del sistema. Mantener sincronizado con el backend
 * (`deca-backend/src/auth/roles.enum.ts`).
 */
const ROLES: { value: UsuarioRol; label: string; color: string }[] = [
  { value: 'ADMIN', label: 'Administrador', color: 'red' },
  { value: 'OPERADOR', label: 'Operador', color: 'deca' },
  { value: 'CONSULTA', label: 'Consulta (auditoría)', color: 'blue' },
  { value: 'CONDUCTOR', label: 'Conductor', color: 'gray' },
];

interface FormValues {
  email: string;
  name: string;
  role: UsuarioRol;
  password: string;
}

const initialFormValues: FormValues = {
  email: '',
  name: '',
  role: 'OPERADOR',
  password: '',
};

export function UsuariosList() {
  const { data: usuarios = [], isLoading, error } = useUsuarios();
  const crear = useCreateUsuario();
  const actualizar = useUpdateUsuario();
  const eliminar = useDeleteUsuario();

  const [opened, { open, close }] = useDisclosure(false);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Usuario | null>(null);
  // El id del usuario actual (mock). En el backoffice real se obtendra de
  // /auth/me. Aqui usamos el email demo@deca.local para identificar al ADMIN
  // logueado y bloquear auto-borrado / auto-desactivacion.
  const currentUserEmail = 'demo@deca.local';

  const filtered = useMemo(
    () =>
      usuarios.filter((u) =>
        [u.email, u.name, u.role]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(filter.toLowerCase()),
      ),
    [usuarios, filter],
  );

  const form = useForm<FormValues>({
    initialValues: initialFormValues,
    validate: {
      email: (v) =>
        /^[^\s@]+@[^\s@]+$/.test(v.trim())
          ? null
          : 'Email no parece válido (ej. usuario@empresa.com)',
      name: (v) => (v.trim().length >= 2 ? null : 'Mínimo 2 caracteres'),
      password: (v) => {
        // Solo obligatoria en alta. En edicion puede venir vacía.
        if (!editingId && v.length < 8) {
          return 'Mínimo 8 caracteres';
        }
        if (editingId && v.length > 0 && v.length < 8) {
          return 'Si la indicas, mínimo 8 caracteres';
        }
        return null;
      },
    },
  });

  /**
   * Sincroniza el formulario al abrir el modal: precarga en edicion,
   * resetea en alta. Importante: NO usamos el spread del form.reset()
   * porque perderíamos las validaciones. Usamos setValues + resetDirty.
   */
  useEffect(() => {
    if (!opened) return;
    if (editingId) {
      const u = usuarios.find((x) => x.id === editingId);
      if (u) {
        form.setValues({
          email: u.email,
          name: u.name,
          role: u.role,
          password: '',
        });
        form.resetDirty();
      }
    } else {
      form.setValues(initialFormValues);
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editingId]);

  const handleNew = () => {
    setEditingId(null);
    open();
  };

  const handleEdit = (u: Usuario) => {
    setEditingId(u.id);
    open();
  };

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    eliminar.mutate(toDelete.id, {
      onSuccess: () => {
        notifications.show({
          title: 'Usuario eliminado',
          message: `${toDelete.email} ya no tiene acceso.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        setToDelete(null);
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al eliminar',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  const handleToggleActivo = (u: Usuario) => {
    if (u.email === currentUserEmail) {
      notifications.show({
        title: 'Acción bloqueada',
        message: 'No puedes desactivar tu propio usuario.',
        color: 'orange',
      });
      return;
    }
    actualizar.mutate(
      { id: u.id, data: { active: !u.active } },
      {
        onSuccess: () =>
          notifications.show({
            title: u.active ? 'Usuario desactivado' : 'Usuario reactivado',
            message: u.email,
            color: 'green',
            icon: <IconCheck size={18} />,
          }),
        onError: (err: unknown) =>
          notifications.show({
            title: 'Error al cambiar el estado',
            message: getErrorMessage(err),
            color: 'red',
            icon: <IconAlertCircle size={18} />,
          }),
      },
    );
  };

  const handleSubmit = (values: FormValues) => {
    if (editingId) {
      const patch: UpdateUsuarioDto = {
        email: values.email.trim(),
        name: values.name.trim(),
        role: values.role,
      };
      // Solo mandamos password si viene con contenido (>= 8 chars).
      if (values.password && values.password.length >= 8) {
        patch.password = values.password;
      }
      actualizar.mutate(
        { id: editingId, data: patch },
        {
          onSuccess: () => {
            notifications.show({
              title: 'Usuario actualizado',
              message: `${values.email} guardado correctamente.`,
              color: 'green',
              icon: <IconCheck size={18} />,
            });
            close();
          },
          onError: (e: unknown) =>
            notifications.show({
              title: 'Error al actualizar',
              message: getErrorMessage(e),
              color: 'red',
              icon: <IconAlertCircle size={18} />,
            }),
        },
      );
      return;
    }

    const payload: CreateUsuarioDto = {
      email: values.email.trim(),
      name: values.name.trim(),
      role: values.role,
      password: values.password,
    };
    crear.mutate(payload, {
      onSuccess: () => {
        notifications.show({
          title: 'Usuario creado',
          message: `${values.email} ya puede iniciar sesión.`,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        close();
      },
      onError: (e: unknown) =>
        notifications.show({
          title: 'Error al crear el usuario',
          message: getErrorMessage(e),
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        }),
    });
  };

  return (
    <Stack gap="md">
      <CatalogPageHeader
        title="Usuarios del sistema"
        subtitle="Personas con acceso al SaaS DeCA. ADMIN puede dar de alta y cambiar roles."
        icon={IconUser}
        newLabel="Nuevo usuario"
        count={usuarios.length}
        onNew={handleNew}
        filters={
          <TextInput
            placeholder="Buscar por email, nombre o rol..."
            leftSection={<IconSearch size={16} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            w={300}
          />
        }
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="No se pudieron cargar los usuarios"
        >
          Verifica tu conexión y vuelve a intentarlo.
        </Alert>
      )}

      <Card withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Email</Table.Th>
              <Table.Th>Nombre</Table.Th>
              <Table.Th>Rol</Table.Th>
              <Table.Th>Email verificado</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th style={{ width: 90, textAlign: 'right' }}>Acciones</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={6}>Cargando...</Table.Td>
              </Table.Tr>
            )}
            {!isLoading && !error && filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>No hay usuarios. Da de alta el primero.</Table.Td>
              </Table.Tr>
            )}
            {filtered.map((u) => {
              const rol = ROLES.find((r) => r.value === u.role);
              const isCurrent = u.email === currentUserEmail;
              return (
                <Table.Tr key={u.id}>
                  <Table.Td>
                    <Group gap={4} align="center">
                      <Text size="sm" ff="monospace">
                        {u.email}
                      </Text>
                      {isCurrent && (
                        <Badge size="xs" variant="light" color="deca">
                          tú
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{u.name}</Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={rol?.color ?? 'gray'}>
                      {rol?.label ?? u.role}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {u.emailVerified ? (
                      <Badge size="xs" variant="light" color="deca">
                        Sí
                      </Badge>
                    ) : (
                      <Badge size="xs" variant="light" color="yellow">
                        Pendiente
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge activo={u.active} />
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Menu position="bottom-end" withinPortal shadow="md">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label="Acciones">
                          <IconChevronDown size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() => handleEdit(u)}
                        >
                          Editar
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconPower size={14} />}
                          onClick={() => handleToggleActivo(u)}
                          disabled={isCurrent}
                        >
                          {u.active ? 'Desactivar' : 'Reactivar'}
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => setToDelete(u)}
                          disabled={isCurrent}
                        >
                          Eliminar
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal
        opened={opened}
        onClose={close}
        title={editingId ? 'Editar usuario' : 'Nuevo usuario'}
        centered
        size="md"
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Email"
              placeholder="usuario@miempresa.com"
              required
              type="email"
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Nombre"
              placeholder="María Gómez"
              required
              {...form.getInputProps('name')}
            />
            <Select
              label="Rol"
              description="Permisos del usuario en el backoffice."
              data={ROLES.map((r) => ({ value: r.value, label: r.label }))}
              required
              allowDeselect={false}
              {...form.getInputProps('role')}
            />
            <PasswordInput
              label={editingId ? 'Nueva contraseña (opcional)' : 'Contraseña inicial'}
              description={
                editingId
                  ? 'Déjala vacía si no quieres resetearla. Mínimo 8 caracteres.'
                  : 'El usuario debería cambiarla en su primer login. Mínimo 8 caracteres.'
              }
              placeholder="TempPass123"
              required={!editingId}
              {...form.getInputProps('password')}
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={close}
                disabled={crear.isPending || actualizar.isPending}
              >
                Cancelar
              </Button>
              <Button type="submit" loading={crear.isPending || actualizar.isPending}>
                {editingId ? 'Guardar cambios' : 'Dar de alta'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <ConfirmDialog
        opened={toDelete !== null}
        title="Eliminar usuario"
        message={
          toDelete
            ? `¿Eliminar a ${toDelete.email}? Esta acción no se puede deshacer y perderá todo el acceso.`
            : ''
        }
        confirmLabel="Eliminar"
        confirmColor="red"
        loading={eliminar.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </Stack>
  );
}
